import {
	Button,
	Checkbox,
	Container,
	FormControl,
	InputLabel,
	ListSubheader,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { detectBrowser, getDate, useIP, useQuestionnaires } from 'Common';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { SelectChangeEvent } from '@mui/material';
import { Api, createCandidate, updateLifeCycle } from 'apis';
import QuestionListModal from './QuestionListModal';
import Loading from 'components/Loading';
import { useReferenceStore } from 'components/NewReferenceCheck';
import { toast } from 'react-toastify';
import { useBillingInfo } from 'pages/user/new-request-v2/hooks/hooks';
import { FuturePaymentBox } from 'pages/user/new-request-v2/FuturePaymentBox';
import { useAuth } from 'contexts/AuthContext';
import Cookies from 'js-cookie';

const requestSchema = z.object({
	firstName: z.string().min(1, 'First name is required'),
	lastName: z.string().min(1, 'Last name is required'),
	email: z.string().email('Invalid email format'),
	jobTitle: z.string().min(1, 'Job title is required'),
	phoneNumber: z.string(),
	is_sms_allow: z.boolean(),
	questionnaire: z.string().min(1, 'Please select a questionnaire'),
	minReference: z.number().min(1, 'At least one reference is required'),
});

type FormDataType = z.infer<typeof requestSchema>;

// Helper function to detect if we're in an iframe
const isInIframe = () => {
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
};

const CandidateForm = () => {
	const [formData, setFormData] = useState<FormDataType>({
		firstName: '',
		lastName: '',
		email: '',
		jobTitle: '',
		phoneNumber: '',
		questionnaire: '',
		is_sms_allow: false,
		minReference: 2,
	});
	const [isDataFetching, setIsDataFetching] = useState(true);
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [isBillingRefreshing, setIsBillingRefreshing] = useState(false);

	const [errors, setErrors] = useState<
		Partial<Record<keyof FormDataType, { _errors: string[] }>>
	>({});

	const navigate = useNavigate();
	const [params] = useSearchParams();
	const { setRefreshToken, setType } = useAuth();

	const candidateId = params.get('candidateId');
	const entityId = params.get('EntityID');
	const token = params.get('token');

	const { data: questions, status } = useQuestionnaires();
	const referenceStore = useReferenceStore();
	const { data: ipAddress } = useIP();
	const { data: billingData, refetch: refetchBillingData } = useBillingInfo();

	// Helper function to check if payment is needed
	const needsPayment = (billingData: any) => {
		const result =
			!billingData?.data ||
			!billingData?.data.Customer ||
			billingData?.data.Customer === null ||
			billingData?.data.Customer === '';

		// Debug logging for payment check
		console.log('needsPayment check:', {
			hasData: !!billingData?.data,
			hasCustomer: !!billingData?.data?.Customer,
			customerId: billingData?.data?.Customer,
			result,
		});

		return result;
	};

	// Helper function to refresh billing data with loading state
	// Used to update payment status after successful payment completion
	const refreshBillingData = async () => {
		setIsBillingRefreshing(true);
		try {
			await refetchBillingData();
		} finally {
			setIsBillingRefreshing(false);
		}
	};

	// Multi-layered approach to ensure payment status is refreshed after successful payment:
	// 1. PostMessage listener - Real-time updates from payment success page
	// 2. Component mount refresh - Handles page refreshes after payment
	// 3. LocalStorage fallback - Backup method if postMessage fails
	// 4. Focus event listener - Updates when iframe regains focus

	// Listen for payment success messages (Method 1)
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (
				event.data.type === 'PAYMENT_SUCCESS' &&
				event.data.source === 'refme_payment'
			) {
				console.log(
					'Payment success message received, refreshing billing data...'
				);
				// Refresh billing data when payment is successful
				refreshBillingData();
				// Force another refresh after a short delay to ensure it's updated
				setTimeout(() => {
					console.log(
						'Secondary billing data refresh after payment message...'
					);
					refreshBillingData();
				}, 1000);
				// Also force a third refresh after longer delay
				setTimeout(() => {
					console.log(
						'Third billing data refresh to ensure payment is detected...'
					);
					refreshBillingData();
				}, 3000);
				toast.success(
					'Payment successful! You can now proceed with your request.'
				);
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, []);

	// Auto-refresh billing data when component mounts (Method 2)
	useEffect(() => {
		const isPartnerIntegration = candidateId || entityId || token;
		if (isPartnerIntegration) {
			// Refresh billing data when partner integration loads
			// This handles cases where payment was completed in another tab
			console.log('Partner integration detected, refreshing billing data...');
			refreshBillingData();
		}
	}, [candidateId, entityId, token]);

	// Periodic check to ensure billing data is up to date (Method 5)
	// This helps catch cases where payment was completed but billing data wasn't updated
	useEffect(() => {
		const isPartnerIntegration = candidateId || entityId || token;
		if (!isPartnerIntegration) return;

		const interval = setInterval(() => {
			// Only refresh if we're currently showing the payment page
			if (needsPayment(billingData) && !isBillingRefreshing) {
				console.log('Periodic billing data refresh while on payment page...');
				refreshBillingData();
			}
		}, 10000); // Check every 10 seconds

		return () => clearInterval(interval);
	}, [candidateId, entityId, token, billingData, isBillingRefreshing]);

	// Check for payment success flag in localStorage (Method 3)
	useEffect(() => {
		const paymentSuccess = localStorage.getItem('payment_success');
		if (paymentSuccess === 'true') {
			console.log('Payment success flag detected, refreshing billing data...');
			// Clear the flag
			localStorage.removeItem('payment_success');
			// Refresh billing data multiple times to ensure it's updated
			refreshBillingData();
			// Force another refresh after a short delay
			setTimeout(() => {
				console.log('Secondary billing data refresh after payment success...');
				refreshBillingData();
			}, 1000);
			toast.success(
				'Payment successful! You can now proceed with your request.'
			);
		}
	}, []);

	// Refresh billing data when user returns from another tab/page (Method 4)
	// This handles when user returns after making payment in another tab
	useEffect(() => {
		const isPartnerIntegration = candidateId || entityId || token;
		if (isPartnerIntegration) {
			const handleFocus = () => {
				// Check if we don't have customer data or customer ID is null and refresh
				if (needsPayment(billingData)) {
					console.log(
						'User returned from another tab, checking payment status...'
					);
					refreshBillingData();
					// Force another refresh after a short delay to ensure it's updated
					setTimeout(() => {
						console.log('Secondary billing data refresh on focus...');
						refreshBillingData();
					}, 1500);
				}
			};

			// Handle tab visibility changes (more reliable than focus in some browsers)
			const handleVisibilityChange = () => {
				if (!document.hidden && needsPayment(billingData)) {
					console.log('Tab became visible, checking payment status...');
					refreshBillingData();
					// Force another refresh after a short delay to ensure it's updated
					setTimeout(() => {
						console.log(
							'Secondary billing data refresh on visibility change...'
						);
						refreshBillingData();
					}, 1500);
				}
			};

			// Handle page show events (when user returns from another page/tab)
			const handlePageShow = () => {
				if (needsPayment(billingData)) {
					console.log('Page shown, checking payment status...');
					refreshBillingData();
				}
			};

			window.addEventListener('focus', handleFocus);
			window.addEventListener('pageshow', handlePageShow);
			document.addEventListener('visibilitychange', handleVisibilityChange);

			return () => {
				window.removeEventListener('focus', handleFocus);
				window.removeEventListener('pageshow', handlePageShow);
				document.removeEventListener(
					'visibilitychange',
					handleVisibilityChange
				);
			};
		}
	}, [
		candidateId,
		entityId,
		token,
		billingData?.data?.Customer,
		refreshBillingData,
	]);

	// OAuth completion detection via postMessage
	useEffect(() => {
		const isPartnerIntegration = candidateId || entityId || token;
		if (!isPartnerIntegration) return;

		// Listen for OAuth completion messages from popup window
		const handleOAuthMessage = (event: MessageEvent) => {
			if (
				event.data.type === 'OAUTH_COMPLETED' &&
				event.data.source === 'refme_oauth'
			) {
				const { provider } = event.data;
				console.log(
					`OAuth completion detected for provider: ${provider}, refetching candidate data...`
				);
				toast.success('OAuth completed successfully!');

				// Refetch candidate data after OAuth completion
				if (token && candidateId) {
					getCandidate(candidateId, 'vincere');
				} else if (candidateId) {
					getCandidate(candidateId, 'jobadder');
				} else if (entityId) {
					getCandidate(entityId, 'bullhorn');
				}
			}
		};

		window.addEventListener('message', handleOAuthMessage);

		return () => {
			window.removeEventListener('message', handleOAuthMessage);
		};
	}, [candidateId, entityId, token]);

	const getCandidate = async (
		candidateId: string,
		partner: 'jobadder' | 'bullhorn' | 'vincere'
	) => {
		setIsDataFetching(true);
		try {
			if (partner === 'jobadder') {
				const response = await Api.get(
					`/api/get-candidaterefme/${candidateId}`
				).catch(err => {
					const authUrl = err?.response?.data?.auth_url;

					if (authUrl) {
						toast.info(
							'OAuth authentication required. Please complete the authentication process in the new tab.',
							{ autoClose: 5000 }
						);
						const newTab = window.open(
							authUrl,
							'_blank',
							'noopener,noreferrer'
						);
					}
				});

				if (response?.data) {
					const candidate = response.data;
					const formattedData = {
						firstName: candidate.firstName,
						lastName: candidate.lastName,
						email: candidate.email,
						phoneNumber: candidate.mobile,
						jobTitle: candidate?.employment?.current?.position || '',
					};
					setFormData(prev => ({ ...prev, ...formattedData }));
				}
			}
			if (partner === 'bullhorn') {
				const response = await Api.get(
					`/api/get-candidatebullhorn/${candidateId}`
				).catch(err => {
					const authUrl = err?.response?.data?.auth_url;

					if (authUrl) {
						toast.info(
							'OAuth authentication required. Please complete the authentication process in the new tab.',
							{ autoClose: 5000 }
						);
						const newTab = window.open(
							authUrl,
							'_blank',
							'noopener,noreferrer'
						);
					}
				});

				if (response?.data) {
					const candidate = response.data.data;
					if (candidate) {
						const formattedData = {
							firstName: candidate.firstName,
							lastName: candidate.lastName,
							email: candidate.email,
							phoneNumber: candidate.mobile,
							jobTitle: candidate.occupation || '',
						};
						setFormData(prev => ({ ...prev, ...formattedData }));
					}
				}
			}
			if (partner === 'vincere') {
				const response = await Api.post<any>(
					`/api/get-candidatevincere/${candidateId}`,
					{ token }
				).catch(err => {
					const authUrl = err?.response?.data?.auth_url;

					if (authUrl) {
						toast.info(
							'OAuth authentication required. Please complete the authentication process in the new tab.',
							{ autoClose: 5000 }
						);
						const newTab = window.open(
							authUrl,
							'_blank',
							'noopener,noreferrer'
						);
					}
				});

				if (response?.data) {
					const candidate = response.data;

					if (candidate) {
						const formattedData = {
							firstName: candidate.first_name,
							lastName: candidate.last_name,
							email: candidate.email,
							phoneNumber: candidate.phone,
							jobTitle: candidate.job_title,
						};
						setFormData(prev => ({ ...prev, ...formattedData }));
					}
				}
			}
		} catch (err) {
			console.log(err);
		} finally {
			setIsDataFetching(false);
		}
	};

	useEffect(() => {
		if (token && candidateId) {
			getCandidate(candidateId, 'vincere');
			return;
		}
		if (candidateId) {
			getCandidate(candidateId, 'jobadder');
			return;
		}
		if (entityId) {
			getCandidate(entityId, 'bullhorn');
			return;
		}
	}, [params]);

	const handleChange = (
		e: React.ChangeEvent<{ name?: string; value: unknown }>
	) => {
		const { name, value } = e.target;
		if (name) {
			setFormData(prev => ({ ...prev, [name]: value }));
		}
	};

	const handleSelectChange = (e: SelectChangeEvent<number | string>) => {
		setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Check if customer data is missing or customer ID is null/empty
		if (needsPayment(billingData)) {
			toast.error('Payment method not added.');
			return;
		}

		const validationResult = requestSchema.safeParse({
			...formData,
			minReference: Number(formData.minReference),
		});

		if (!validationResult.success) {
			const formattedErrors = validationResult.error.format();
			setErrors(formattedErrors as typeof errors);
		} else {
			setErrors({});

			const on_cc_contactsUUID = referenceStore.contactsList
				.filter(contact => contact.isccresults)
				.map(contact => contact.uuid);
			try {
				setIsFormSubmitting(true);
				// form submission logic
				const resp = await createCandidate({
					email: formData.email,
					recruiter: referenceStore.recruiter,
					firstName: formData.firstName.trim(),
					lastName: formData.lastName.trim(),
					phone: formData.phoneNumber,
					role: formData.jobTitle,
					questionnaire: formData.questionnaire,
					requestDate: referenceStore.requestDate,
					response: referenceStore.response,
					recruiterName: referenceStore.recruiter,
					recruiterTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
					min_reference: formData.minReference,
					is_sms_allow: formData.is_sms_allow,
					cc_email: on_cc_contactsUUID,
					refme_candidate_id: candidateId && !token ? candidateId : '',
					bullhorn_candidate_id: params.get('EntityID'),
					vincere_candidate_id: candidateId && token ? candidateId : '',
				});

				const body = {
					uuid: resp.data.uuid,
					name: resp.data.recruiterName,
					userType: 'recruiter',
					date: getDate(),
					action: 'Sent request to Candidate',
					osBrowser: detectBrowser(),
					ipAddress: !!ipAddress ? ipAddress : null,
					locationISP: localStorage.getItem('cityName'),
					refereeUUID: resp.data.uuid,
				};
				await updateLifeCycle(body);
				toast.success('Reference submitted successfully!');
				navigate('/referenceSubmitted');
			} catch (e: any) {
				toast.error(e.message! ?? 'Something went wrong!');
			} finally {
				setIsFormSubmitting(false);
			}
			console.log('Form submitted successfully', formData);
		}
	};

	if (isDataFetching) {
		return <Loading />;
	}

	// Check if customer data is missing or customer ID is null/empty
	const paymentNeeded = needsPayment(billingData);

	// Debug logging to understand payment status
	console.log('Payment status check:', {
		paymentNeeded,
		customerExists: !!billingData?.data?.Customer,
		customerId: billingData?.data?.Customer?.id,
		isBillingRefreshing,
		billingDataExists: !!billingData,
	});

	if (paymentNeeded) {
		return (
			<Container>
				{isBillingRefreshing ? (
					<div className='py-8 text-center'>
						<Loading />
						<p className='mt-2 text-gray-600'>Checking payment status...</p>
					</div>
				) : (
					<div>
						<FuturePaymentBox newTab />
						<div className='mt-4 text-center'>
							<button
								className='text-sm text-blue-600 underline hover:text-blue-800'
								onClick={() => {
									console.log('Manual refresh button clicked...');
									refreshBillingData();
								}}
							>
								Already paid? Click here to refresh payment status
							</button>
						</div>
					</div>
				)}
			</Container>
		);
	}

	return (
		<div className='px-3'>
			<form onSubmit={handleSubmit}>
				<div>
					<h4>Candidate Details</h4>
					<div className='flex flex-col gap-4'>
						<div className='flex gap-3'>
							<TextField
								className='flex-1'
								size='small'
								label='First name'
								name='firstName'
								value={formData.firstName}
								onChange={handleChange}
								error={!!errors.firstName}
								helperText={errors.firstName?.['_errors']?.[0]}
							/>
							<TextField
								className='flex-1'
								size='small'
								label='Last name'
								name='lastName'
								value={formData.lastName}
								onChange={handleChange}
								error={!!errors.lastName}
								helperText={errors.lastName?.['_errors']?.[0]}
							/>
						</div>
						<div className='flex gap-3'>
							<TextField
								className='flex-1'
								size='small'
								label='Email'
								name='email'
								value={formData.email}
								onChange={handleChange}
								error={!!errors.email}
								helperText={errors.email?.['_errors']?.[0]}
							/>
							<TextField
								className='flex-1'
								size='small'
								label='Phone Number'
								name='phoneNumber'
								value={formData.phoneNumber}
								onChange={handleChange}
								error={!!errors.phoneNumber}
								helperText={errors.phoneNumber?.['_errors']?.[0]}
							/>
						</div>
						<TextField
							size='small'
							label='Job Title'
							name='jobTitle'
							value={formData.jobTitle}
							onChange={handleChange}
							error={!!errors.jobTitle}
							helperText={errors.jobTitle?.['_errors']?.[0]}
						/>
						<div className='flex items-center'>
							<input
								id='is_sms_allow'
								className='mt-2 accent-[#F621A4]'
								type='checkbox'
								checked={formData.is_sms_allow}
								onChange={e =>
									setFormData(prev => ({
										...prev,
										is_sms_allow: !prev.is_sms_allow,
									}))
								}
							/>
							<label
								className='ml-2 pb-2'
								htmlFor='is_sms_allow'
							>
								Send Via SMS
							</label>
						</div>
					</div>

					<h4>Questionnaire</h4>
					<div className='flex gap-4'>
						<FormControl
							className='flex-1'
							error={!!errors.questionnaire}
						>
							<InputLabel
								id='question-label'
								size='small'
							>
								Select Questionnaire
							</InputLabel>
							<Select
								labelId='question-label'
								id='question'
								name='questionnaire'
								value={formData.questionnaire}
								onChange={handleSelectChange}
								label='Select Questionnaire'
								size='small'
							>
								<ListSubheader>Corporate Questionnaires</ListSubheader>
								{questions
									?.filter(question => !question.is_client_created)
									?.map(question => (
										<MenuItem
											key={question.uuid}
											value={question.uuid}
										>
											{question.questionnaire_title}
										</MenuItem>
									))}
								<ListSubheader>My Questionnaires</ListSubheader>
								{questions
									?.filter(question => question.is_client_created)
									?.map(question => (
										<MenuItem
											key={question.uuid}
											value={question.uuid}
										>
											{question.questionnaire_title}
										</MenuItem>
									))}
							</Select>
							{errors.questionnaire && (
								<p className='text-sm text-red-500'>
									{errors.questionnaire['_errors']?.[0]}
								</p>
							)}
							{formData.questionnaire && (
								<div className='text-sm'>
									<a
										href='#'
										onClick={() => setIsOpen(true)}
									>
										Preview{' '}
										{
											questions?.find(q => q.uuid === formData.questionnaire)
												?.questionnaire_title
										}
									</a>
								</div>
							)}
							<a
								className='pt-2 text-sm'
								target='_blank'
								href='/home/template-homepage'
							>
								Create Questionnaire
							</a>
						</FormControl>

						<FormControl
							className='min-w-24'
							error={!!errors.minReference}
						>
							<InputLabel
								id='minreference-label'
								size='small'
							>
								Min Reference
							</InputLabel>
							<Select
								labelId='minreference-label'
								id='minreference'
								name='minReference'
								value={formData.minReference}
								onChange={handleSelectChange}
								label='Min Reference'
								size='small'
							>
								{[1, 2, 3, 4, 5, 6].map(index => (
									<MenuItem
										key={index}
										value={index}
									>
										{index}
									</MenuItem>
								))}
							</Select>
							{errors.minReference && (
								<p className='text-sm text-red-500'>
									{errors.minReference['_errors']?.[0]}
								</p>
							)}
						</FormControl>
					</div>
					<QuestionListModal
						open={isOpen}
						setOpen={setIsOpen}
						quesionnaire={
							questions?.find(q => q.uuid === formData.questionnaire)
								?.questionList || []
						}
					/>
				</div>

				{!needsPayment(billingData) && (
					<div className='mt-3 w-full'>
						<Button
							loading={isFormSubmitting}
							fullWidth
							variant='contained'
							type='submit'
						>
							Send Request
						</Button>
					</div>
				)}
			</form>
		</div>
	);
};

export default CandidateForm;
